/**
 * Trade Audio — sound functions for order fills.
 *
 * Three tiers:
 * - playFillTone()     — standard fill (warm two-voice sine+triangle, 150ms)
 * - playKaChingTone()  — first fill of a resting order (cash register hit+ring, 330ms)
 * - playWhaleTone()    — large fill 1000+ contracts (noise burst + low sweep, 400ms)
 */

import { playSynthSound } from './audioContext';
import { readSoundSettings } from './soundSettings';
import type { KalshiFill } from '../../types/kalshi';
import { boundaryWarn } from '../infra/boundaryLog';

/** Standard fill — warm two-voice: sine 600Hz + triangle 900Hz, 150ms */
export function playFillTone(): void {
  boundaryWarn('sound', 'playFillTone');
  playSynthSound([
    { type: 'sine', freq: 600, duration: 0.15, amplitude: 0.3 },
    { type: 'triangle', freq: 900, duration: 0.15, amplitude: 0.2 },
  ]);
}

/** Ka-ching — first fill: metallic hit (square 2kHz, 30ms) + bright ring (sine 1.5k + 2k, 300ms) */
export function playKaChingTone(): void {
  boundaryWarn('sound', 'playKaChingTone');
  playSynthSound([
    { type: 'square', freq: 2000, duration: 0.03, amplitude: 0.25, attack: 0.002, decay: 0.03 },
    { type: 'sine', freq: 1500, duration: 0.3, amplitude: 0.2, attack: 0.01, decay: 0.28 },
    { type: 'sine', freq: 2000, duration: 0.3, amplitude: 0.15, attack: 0.01, decay: 0.28 },
  ]);
}

/** Whale fill (1000+ contracts) — noise burst + low sine sweep 200→80Hz, 400ms */
export function playWhaleTone(): void {
  boundaryWarn('sound', 'playWhaleTone');
  playSynthSound([
    { type: 'noise', freq: 0, duration: 0.05, amplitude: 0.3, attack: 0.005, decay: 0.04 },
    {
      type: 'sine',
      freq: 200,
      duration: 0.4,
      amplitude: 0.35,
      freqSweep: 80,
      attack: 0.01,
      decay: 0.38,
    },
  ]);
}

/** Order placed — ascending confirmation chirp: sine 800→1200Hz + triangle 1000Hz, 150ms */
export function playOrderPlacedTone(): void {
  boundaryWarn('sound', 'playOrderPlacedTone');
  playSynthSound([
    { type: 'sine', freq: 800, duration: 0.15, amplitude: 0.25, freqSweep: 1200, attack: 0.01, decay: 0.13 },
    { type: 'triangle', freq: 1000, duration: 0.15, amplitude: 0.15, attack: 0.01, decay: 0.13 },
  ]);
}

/** Queue warning (queue dropped below 50k) — two soft mid pips at 740Hz, 80ms
 *  each with a 120ms gap. A double-pip "heads-up" pattern: deliberately NOT the
 *  single warm chime of a fill (600+900Hz) — The Professor flagged the old
 *  660+990Hz chime as indistinguishable from a contract sell — and a gentler
 *  sibling of the urgent queue-critical sweep double-chirp it escalates into. */
export function playQueueWarningTone(): void {
  boundaryWarn('sound', 'playQueueWarningTone');
  playSynthSound([
    { type: 'sine', freq: 740, duration: 0.08, amplitude: 0.3, attack: 0.005, decay: 0.07 },
    { type: 'square', freq: 740, duration: 0.08, amplitude: 0.08, attack: 0.005, decay: 0.07 },
    { type: 'sine', freq: 740, duration: 0.08, amplitude: 0.3, attack: 0.005, decay: 0.07, delay: 0.2 },
    { type: 'square', freq: 740, duration: 0.08, amplitude: 0.08, attack: 0.005, decay: 0.07, delay: 0.2 },
  ]);
}

/** Queue critical — urgent double chirp 880→1320Hz, 280ms (queue dropped below 10k) */
export function playQueueCriticalTone(): void {
  boundaryWarn('sound', 'playQueueCriticalTone');
  playSynthSound([
    { type: 'sine', freq: 880, duration: 0.12, amplitude: 0.32, freqSweep: 1320, attack: 0.005, decay: 0.1 },
    { type: 'square', freq: 1100, duration: 0.12, amplitude: 0.18, attack: 0.005, decay: 0.1 },
    { type: 'sine', freq: 880, duration: 0.12, amplitude: 0.32, freqSweep: 1320, attack: 0.005, decay: 0.1, delay: 0.16 },
    { type: 'square', freq: 1100, duration: 0.12, amplitude: 0.18, attack: 0.005, decay: 0.1, delay: 0.16 },
  ]);
}

/** Fell behind — a new better-priced level formed ABOVE your order (queueAbove
 *  crossed the way-behind threshold), so you've slipped back in line. A gentle
 *  DESCENDING double note (560→440 then 520→400Hz) — deliberately the inverse of
 *  the ascending queue-advancing pips, and softer/higher than the harsh order-
 *  rejected descent, so "I fell behind" reads distinctly. (StreamRift #5.) */
export function playFellBehindTone(): void {
  boundaryWarn('sound', 'playFellBehindTone');
  playSynthSound([
    { type: 'sine', freq: 560, duration: 0.1, amplitude: 0.26, freqSweep: 440, attack: 0.005, decay: 0.09 },
    { type: 'triangle', freq: 460, duration: 0.1, amplitude: 0.1, attack: 0.005, decay: 0.09 },
    { type: 'sine', freq: 520, duration: 0.1, amplitude: 0.26, freqSweep: 400, attack: 0.005, decay: 0.09, delay: 0.14 },
  ]);
}

/** Cross-venue divergence — Polymarket's mid has pulled >= 2¢ away from Kalshi
 *  on a market you're in. Two bell tones a fifth apart (700Hz then 1050Hz, the
 *  upper struck a beat later) — a "split" that reads as two prices separating,
 *  deliberately neither the same-pitch queue pips nor the ascending queue-
 *  critical sweep, so the trader hears "the venues disagree" distinctly. */
export function playDivergenceTone(): void {
  boundaryWarn('sound', 'playDivergenceTone');
  playSynthSound([
    { type: 'sine', freq: 700, duration: 0.18, amplitude: 0.28, attack: 0.005, decay: 0.16 },
    { type: 'triangle', freq: 700, duration: 0.18, amplitude: 0.08, attack: 0.005, decay: 0.16 },
    { type: 'sine', freq: 1050, duration: 0.2, amplitude: 0.26, attack: 0.005, decay: 0.18, delay: 0.1 },
    { type: 'triangle', freq: 1050, duration: 0.2, amplitude: 0.07, attack: 0.005, decay: 0.18, delay: 0.1 },
  ]);
}

/** Order rejected — low descending two-tone: sine 400→200Hz + square 300→150Hz, 250ms */
export function playOrderRejectedTone(): void {
  boundaryWarn('sound', 'playOrderRejectedTone');
  playSynthSound([
    {
      type: 'sine',
      freq: 400,
      duration: 0.25,
      amplitude: 0.25,
      freqSweep: 200,
      attack: 0.01,
      decay: 0.23,
    },
    {
      type: 'square',
      freq: 300,
      duration: 0.25,
      amplitude: 0.15,
      freqSweep: 150,
      attack: 0.01,
      decay: 0.23,
    },
  ]);
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Replay fill sounds sequentially from an array of fills.
 * Sequence: ka-ching → fill → whale so all tiers are tested.
 * Checks sound settings before each tone — bails if user toggles off mid-replay.
 */
export async function replayFillSounds(fills: KalshiFill[]): Promise<void> {
  for (let i = 0; i < fills.length; i++) {
    const settings = readSoundSettings();
    if (!settings.tradeSoundEnabled) return;

    const isLast = i === fills.length - 1;
    if (i === 0) {
      playKaChingTone();
    } else if (isLast) {
      playWhaleTone();
    } else {
      playFillTone();
    }

    if (i < fills.length - 1) {
      await sleep(800);
    }
  }
}
